import { listOfProject } from "Api/Project";
import { editQuotationStatusApi, listOfQuotationApi, quotationPdfApi } from "Api/Quotation";
import BaseButton from "Components/Base/BaseButton";
import BaseInput from "Components/Base/BaseInput";
import Loader from "Components/Base/BaseLoader";
import BaseModal from "Components/Base/BaseModal";
import TableContainer from "Components/Base/BaseTable";
import DeleteModal from "Components/Base/DeleteModal";
import withRouter from "Components/Common/withRouter";
import {
  Admin,
  checkStatusCodeSuccess,
  DownloadLabel,
  notFound,
  pdfDownload,
  rupeeSymbol,
  Search,
} from "Components/constants/Common";
import {
  QuotationKey,
  QuotationLabel,
} from "Components/constants/Quotation";
import {
  ActionKey,
  ActionLabel,
  editLabel,
  NewLabel,
  NoLabel,
  StatusLabel,
  StatusUpdateMessageLabel,
  YesLabel,
} from "Components/constants/Role";
import { getItem } from "Components/constants/SessionStores";
import { InputPlaceHolder, validationMessages } from "Components/constants/validation";
import { useFormik } from "formik";
import { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as yup from "yup";

const Quotation = (props: any) => {
  document.title = QuotationLabel.QuotationTitle;
  const navigate = useNavigate();
  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [quotationData, setQuotationData] = useState<Array<any>>([]);
  const [editModal, setEditModal] = useState<boolean>(false);
  const [quotationId, setQuotationId] = useState<number | null>(null);
  const [pageLoader, setPageLoader] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [quotationSearchData, setQuotationSearchData] = useState<Array<any>>([]);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState(0);
  const [columnName, setColumnName] = useState("id");
  const [sortOrder, setSortOrder] = useState("desc");
  const [status, setStatus] = useState<string | null>(null);

  const fetchQuotation = async () => {
    const payload = {
      pageSize,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfQuotationApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setQuotationData(res?.data?.quotationList || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setQuotationData([]);
        }
      })
      .catch(() => {
        setQuotationData([]);
      })
      .finally(() => setLoader(false));
  };

  const fetchSearchQuotation = () => {
    const payload = {
      pageSize,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };
    setLoader(true);
    listOfQuotationApi(payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          setQuotationSearchData(res?.data?.quotationList || []);
          setTotalRecords(res?.data?.totalItems || 0);
          setTotalPages(res?.data?.totalPages);
          setCurrentPage(res?.data?.currentPage);
          setTotalNumberOfRows(res?.data?.numberOfRows);
          toast.success(res?.message);
        } else {
          setQuotationSearchData([]);
        }
      })
      .catch(() => {
        setQuotationSearchData([]);
      })
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    if (searchValue) {
      fetchSearchQuotation();
    } else {
      fetchQuotation();
    }
  }, [currentPage, pageSize, searchValue, sortOrder, columnName]);

  const handleStatusModal = (id: number, currentStatus: string) => {
    setQuotationId(id);
    setEditModal(true);
    setStatus(currentStatus);
  };

  const closeDeleteModal = () => {
    setEditModal(false);
    setQuotationId(null);
  };

  const validateRemark = yup.object({
    remarks:yup
        .string()
        .required(validationMessages.required(QuotationLabel.Remarks)),
     
  })
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      remarks: null,
    },
    validationSchema: status===QuotationLabel.Rejected && validateRemark,
    onSubmit: (values) => {
      handleEditStatus();
    }
  });

  const handleEditStatus = () => {
    setSubmitLoader(true);
    const payload = {
      status: status,
      ...(status === QuotationLabel.Rejected && {remarks:formik.values.remarks}),
    };
    editQuotationStatusApi(Number(quotationId), payload)
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          fetchQuotation();
          toast.success(res?.message);
          formik.resetForm();
        } else {
          toast.error(res?.message);
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || err?.message);
      })
      .finally(() => {
        setSubmitLoader(false);
        setStatus(null);  
        setEditModal(false);
      });
  };
  const handleSearchValueChange = (value: any) => {
    if (value !== searchValue) {
      setCurrentPage(1);
      setSearchValue(value);
    }
  };

  const handleFetchData = (page: any) => {
    setCurrentPage(page);
  };

  const handleFetchSorting = (
    page: any,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const handleDownload = (row:any) => {
    pdfDownload(
      quotationPdfApi,
      row?.id,
      `quotation_${row?.quotation_number}.pdf`,
      setPageLoader
    );
  };
  
  const quotationColumns = useMemo(
    () => [
      {
        header: QuotationLabel.VendorId,
        accessorKey: QuotationKey.VendorId,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: QuotationLabel.VendorName,
        accessorKey: QuotationKey.VendorName,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: QuotationLabel.QuoteNo,
        accessorKey: QuotationKey.QuoteNo,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: QuotationLabel.QuoteDate,
        accessorKey: QuotationKey.QuoteDate,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: QuotationLabel.QuoteAmtInc,
        accessorKey: QuotationKey.QuoteAmt,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return rupeeSymbol(cell?.row?.original?.with_gst_amount);
        },
      },
      {
        header: QuotationLabel.Status,
        accessorKey: QuotationKey.Status,
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          return (
            <div className="d-flex justify-content-start gap-2 icon">
              <span
                className={`badge ${
                  cell?.row?.original?.status === QuotationLabel.Approved
                    ? "bg-success"
                    : cell?.row?.original?.status === QuotationLabel.Rejected
                    ? "bg-danger"
                    : cell?.row?.original?.status === QuotationLabel.Recheck
                    ? "bg-warning"
                    : "bg-primary"
                }`}
              >
                {cell?.row?.original?.status === QuotationLabel.Approved
                  ? QuotationLabel.Approved
                  : cell?.row?.original?.status === QuotationLabel.Rejected
                  ? QuotationLabel.Rejected
                  : cell?.row?.original?.status === QuotationLabel.Recheck
                  ? QuotationLabel.Recheck
                  : QuotationLabel.Pending}
              </span>
            </div>
          );
        },
      },
      {
        header: ActionLabel,
        accessorKey: ActionKey,
        cell: (cell: any) => {
          const currentStatus = cell?.row?.original?.type;
          return (
            <div className="d-flex justify-content-start enable-action gap-2 icon">
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-download-outline fs-4 text-primary cursor-pointer`}
                  onClick={() => handleDownload(cell?.row?.original)}
                ></i>
                <span className="tooltip-text">{DownloadLabel}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-eye-outline fs-4 text-warning cursor-pointer`}
                  onClick={() => {
                    navigate(`/view-quotation/${cell?.row?.original?.id}`, {
                      state: { viewQuotation: true },
                    });
                  }}
                ></i>
                <span className="tooltip-text">{QuotationLabel.View}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className={`mdi mdi-pencil-outline fs-4 text-info cursor-pointer ${
                    currentStatus === QuotationLabel.Publish && `icon-disabled`
                  }`}
                  onClick={() => {
                    navigate(`/edit-quotation/${cell?.row?.original?.id}`);
                  }}
                ></i>
                <span className="tooltip-text">{editLabel}</span>
              </span>
              {getItem("role") === Admin && cell?.row?.original?.status!==QuotationLabel.Approved && (
                <>
                  {cell?.row?.original?.status !== QuotationLabel.Rejected && (
                    <span className="tooltip-container">
                      <i
                        className={`mdi mdi-close fs-4 cursor-pointer text-danger ${
                          currentStatus === QuotationLabel.Publish &&
                          `icon-disabled`
                        }`}
                        onClick={() =>
                          handleStatusModal(
                            cell?.row?.original?.id,
                            QuotationLabel.Rejected
                          )
                        }
                      ></i>
                      <span className="tooltip-text">
                        {QuotationLabel.Reject}
                      </span>
                    </span>
                  )}
                  {cell?.row?.original?.status !== QuotationLabel.Approved && (
                    <span className="tooltip-container">
                      <i
                        className={`mdi mdi-check fs-4 cursor-pointer text-success ${
                          currentStatus === QuotationLabel.Publish &&
                          `icon-disabled`
                        }`}
                        onClick={() =>
                          handleStatusModal(
                            cell?.row?.original?.id,
                            QuotationLabel.Approved
                          )
                        }
                      ></i>
                      <span className="tooltip-text">
                        {QuotationLabel.Approve}
                      </span>
                    </span>
                  )}
                </>
              )}
            </div>
          );
        },
        enableColumnFilter: false,
        enableSorting: false,
      },
    ],
    []
  );

  return (
    <div className="page-content">
      {pageLoader && <Loader/>}
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">
            {QuotationLabel.QuotationTitle}
          </span>
          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/add-quotation")}
              children={
                <>
                  <i className="mdi mdi-plus" /> {NewLabel}
                </>
              }
            />
          </div>
        </div>

        <div className="card py-2 px-4">
          {loader || quotationData.length > 0 ? (
            <TableContainer
              customPageSize={pageSize}
              setCustomPageSize={setPageSize}
              isGlobalFilter={true}
              columns={quotationColumns}
              data={searchValue ? quotationSearchData : quotationData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              onSearch={handleSearchValueChange}
              totalNumberOfRows={totalNumberOfRows}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
        </div>
        <BaseModal
          open={editModal}
          toggle={() => setEditModal(!editModal)}
          submit={() => formik.handleSubmit()}
          title={StatusLabel}
          closeLabel={NoLabel}
          submitLabel={YesLabel}
          closeLabelColor="danger"
          submitLabelColor="success"
          loader={submitLoader}
          size="sm"
        >
          <>
            <div>{StatusUpdateMessageLabel}</div>
            {status === QuotationLabel.Rejected && (
            <form onSubmit={formik.handleSubmit}>
              <div className="col-md-12 col-sm-12 col-12 mt-2">
                <BaseInput
                  type="textarea"
                  name="remarks"
                  placeholder={InputPlaceHolder(QuotationLabel.Remarks)}
                  label={QuotationLabel.Remarks}
                  value={formik.values.remarks}
                  onChange={formik.handleChange}
                  handleBlur={formik.handleBlur}
                  error={formik.errors.remarks}
                  touched={formik.touched.remarks}
                  required
                />
              </div>
            </form>
            )}
          </>
        </BaseModal>
      </div>
    </div>
  );
};

export default Quotation;
